import { Helmet } from 'react-helmet-async'
import './App.css'
import { AnalyticsEvents } from './firebase'
import { CookieConsent } from './components/CookieConsent'
import { PricingTiers } from './components/PricingTiers'
import { Footer } from './components/layout/PageShell'
import { skipToMain } from './components/layout/skipToMain'

const DOWNLOAD_IOS_URL = '/link?code=landing&by=site&platform=ios'
const DOWNLOAD_ANDROID_URL = '/link?code=landing&by=site&platform=android'
const SIGN_IN_URL = 'https://app.acesense.io/sign-in'
const SIGN_UP_URL = 'https://app.acesense.io/sign-up'

function App() {
  const trackAppStoreClick = (location: string) => {
    AnalyticsEvents.appStoreClick?.(location)
  }

  const trackWebAppClick = (location: string) => {
    AnalyticsEvents.webAppClick?.(location)
  }

  return (
    <div className="app">
      <Helmet>
        <title>AceSense: post-match AI tennis reports on iOS and Android</title>
        <meta name="description" content="Upload a phone-recorded tennis video and get a post-match AI tennis report: shot detection, ball tracking, court heatmap, biomechanics, stroke quality. iOS + Android. EU pricing from €0. The cross-platform SwingVision alternative." />
        <link rel="canonical" href="https://acesense.io/" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:site_name" content="AceSense" />
        <meta property="og:url" content="https://acesense.io/" />
        <meta property="og:title" content="AceSense: post-match AI tennis reports on iOS and Android" />
        <meta property="og:description" content="Upload a phone-recorded tennis video, get a post-match AI tennis report in minutes. Shot detection, biomechanics, court heatmap. iOS + Android, no extra hardware." />
        <meta property="og:image" content="https://acesense.io/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AceSense: post-match AI tennis reports on iOS and Android" />
        <meta name="twitter:description" content="Upload a phone-recorded tennis video, get a post-match AI tennis report in minutes. Shot detection, biomechanics, court heatmap. iOS + Android, no extra hardware." />
        <meta name="twitter:image" content="https://acesense.io/og-image.png" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      {/* Keyboard-only skip link — jumps past the nav straight to the hero. */}
      <a className="skip-link" href="#main-content" onClick={skipToMain}>
        Skip to main content
      </a>

      {/* Header/Navigation */}
      <header className="header">
        <nav className="nav container" aria-label="Main navigation">
          <a href="/" className="logo" aria-label="AceSense home">
            <img
              src="/logo.svg"
              alt=""
              className="logo-image"
              aria-hidden="true"
              width={32}
              height={32}
            />
            <span className="logo-text">AceSense</span>
          </a>
          <div className="nav-links">
            <a href="#features">Features</a>
            <a href="#how-it-works">How it works</a>
            <a href="#pricing">Pricing</a>
          </div>
          <div className="nav-actions">
            <a
              href={SIGN_IN_URL}
              onClick={() => trackWebAppClick('nav-signin')}
              className="btn btn-ghost nav-signin"
            >
              Sign in
            </a>
            <a
              href={SIGN_UP_URL}
              onClick={() => trackWebAppClick('nav-create-account')}
              className="btn btn-primary nav-cta"
              aria-label="Create a free AceSense account"
            >
              Get started
            </a>
          </div>
        </nav>
      </header>

      {/* Hero Section — wrapped in <main> for the screen-reader landmark
          Lighthouse flagged. Same id is reused so the skip-link still
          targets the right place. */}
      <main id="main-content" tabIndex={-1}>
      <section className="hero">
        <div className="hero-glow"></div>
        <div className="container hero-container">
          <div className="hero-content">
            <div className="hero-badge">
              <span className="badge-dot"></span>
              One video · one clear next step
            </div>
            <h1 className="hero-title">
              Your match.<br />
              <span className="text-gradient">Your next move.</span>
            </h1>
            <p className="hero-subtitle">
              Record on your phone. Upload after you play. Get a clear report on what held up, what broke down, and what to train next.
            </p>
            <div className="hero-cta">
              <div className="hero-cta-primary-wrap">
                <a
                  href={SIGN_UP_URL}
                  onClick={() => trackWebAppClick('hero-create-account')}
                  className="btn btn-primary btn-large hero-cta-primary"
                  aria-label="Create a free AceSense account"
                >
                  Analyze my first match
                </a>
                <p className="hero-cta-note">Free plan · no card required</p>
              </div>
              {/* The store badges, the "Then download the app" step label and
                  the install-support note used to sit here too, making seven
                  stacked elements under one headline. They also showed step
                  two before step one was possible: you need an account before
                  the app is any use, which the label itself admitted. The
                  closing CTA section already carries the identical badge block
                  for readers who scroll, and the footer has Contact, so
                  nothing here is a lost path — the hero is now one primary
                  action and one way to see the product first. */}
              <a
                href="/sample-report"
                onClick={() => trackWebAppClick('hero-signup')}
                className="hero-cta-sample"
                aria-label="View a sample AceSense post-match report"
              >
                View sample report
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M5 12H19M19 12L13 6M19 12L13 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </a>
            </div>
            <div className="hero-credentials">
              NVIDIA Inception Member · Almi Backed · Built with 50 players
            </div>
          </div>
          <div className="hero-mockup">
            <article className="match-report-preview" aria-label="Example AceSense post-match report">
              <header className="report-header">
                <div>
                  <span className="report-eyebrow">Sample report</span>
                  <p className="report-meta">Singles · 72m · Today</p>
                </div>
                <span className="report-status"><span aria-hidden="true"></span>Analysis ready</span>
              </header>

              <section className="report-verdict" aria-labelledby="report-verdict-title">
                <p className="report-section-label">Match verdict</p>
                <h2 id="report-verdict-title">Your forehand held up.</h2>
                <p>You created depth under pressure. The backhand broke down first when rallies passed five shots.</p>
              </section>

              <dl className="report-metrics" aria-label="Measured match highlights">
                <div>
                  <dt>Rallies</dt>
                  <dd>67</dd>
                </div>
                <div>
                  <dt>Deep forehands</dt>
                  <dd>12<span>/16</span></dd>
                </div>
                <div>
                  <dt>Stroke quality</dt>
                  <dd>0.92</dd>
                </div>
              </dl>

              <section className="report-pattern" aria-labelledby="report-pattern-title">
                <div className="report-pattern-heading">
                  <div>
                    <p className="report-section-label">Shot pattern</p>
                    <h3 id="report-pattern-title">Forehand placement</h3>
                  </div>
                  <span>92% confidence</span>
                </div>
                <div className="report-court" aria-hidden="true">
                  <svg viewBox="0 0 320 154" xmlns="http://www.w3.org/2000/svg">
                    <rect x="1" y="1" width="318" height="152" rx="5" />
                    <line x1="160" y1="1" x2="160" y2="153" />
                    <line x1="80" y1="1" x2="80" y2="153" />
                    <line x1="240" y1="1" x2="240" y2="153" />
                    <line x1="80" y1="77" x2="240" y2="77" />
                    <line className="report-net" x1="160" y1="0" x2="160" y2="154" />
                    <path className="report-trajectory" d="M42 118 C105 110 130 62 205 35" />
                    <circle className="report-shot report-shot-strong" cx="205" cy="35" r="6" />
                    <circle className="report-shot" cx="225" cy="53" r="5" />
                    <circle className="report-shot" cx="259" cy="39" r="5" />
                    <circle className="report-shot" cx="198" cy="108" r="5" />
                    <circle className="report-shot" cx="274" cy="91" r="5" />
                    <circle className="report-origin" cx="42" cy="118" r="5" />
                  </svg>
                </div>
              </section>

              <section className="report-next" aria-labelledby="report-next-title">
                <div>
                  <p className="report-section-label">Train next</p>
                  <h3 id="report-next-title">Backhand depth under pressure</h3>
                </div>
                <span>10 min</span>
              </section>

              <p className="report-gap"><strong>Honest gap</strong> Spin and ball speed were not measured from this angle.</p>
            </article>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="partners" aria-label="Partners">
        <div className="container">
          <div className="partners-inner">
            <p className="partners-label">Backed by</p>
            <div className="partners-logos">
              <img
                src="/almi-logo-vit-rgb.webp"
                alt="Almi"
                className="partner-logo partner-logo-almi"
                loading="lazy"
                width={120}
                height={40}
              />
            </div>
            <p className="partners-label" style={{ marginTop: 'var(--spacing-xl)' }}>Member of</p>
            <div className="partners-logos">
              <img
                src="/nvidia-inception-program-badge-rgb-for-screen.svg"
                alt="NVIDIA Inception Program"
                className="partner-logo partner-logo-nvidia"
                loading="lazy"
                width={150}
                height={40}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features section">
        <div className="container">
          <div className="section-header text-center">
            <span className="section-tag">Features</span>
            <h2>Everything in the<br /><span className="text-gradient">post-match report</span></h2>
          </div>
          <div className="features-grid">
            <div className="feature-card animate-on-scroll">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                  <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </div>
              <h3>Shot detection</h3>
              <p>Automatically find and label every shot: forehands, backhands, serves, volleys.</p>
            </div>
            <div className="feature-card animate-on-scroll animate-delay-80">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h3>Ball tracking</h3>
              <p>TrackNet-based trajectory tracking finds where every ball bounces on court.</p>
            </div>
            <div className="feature-card animate-on-scroll animate-delay-160">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                  <path d="M2 12H22" stroke="currentColor" strokeWidth="2" />
                  <path d="M12 2C14.5013 4.73835 15.9228 8.29203 16 12C15.9228 15.708 14.5013 19.2616 12 22" stroke="currentColor" strokeWidth="2" />
                </svg>
              </div>
              <h3>Stroke quality</h3>
              <p>Pose-based scores for preparation, contact, and follow-through on every shot.</p>
            </div>
            <div className="feature-card animate-on-scroll animate-delay-240">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                  <path d="M3 12H21" stroke="currentColor" strokeWidth="2" />
                  <path d="M12 3V21" stroke="currentColor" strokeWidth="2" />
                </svg>
              </div>
              <h3>Court heatmap</h3>
              <p>See where your shots actually land, filterable by shot type, serve, and outcome.</p>
            </div>
            <div className="feature-card animate-on-scroll animate-delay-320">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M20 12C20 16.4183 16.4183 20 12 20C7.58172 20 4 16.4183 4 12C4 7.58172 7.58172 4 12 4C16.4183 4 20 7.58172 20 12Z" stroke="currentColor" strokeWidth="2" />
                </svg>
              </div>
              <h3>Coaching report</h3>
              <p>A coach-readable report with your top patterns and the next thing to work on.</p>
            </div>
            <div className="feature-card animate-on-scroll animate-delay-400">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M22 12H18L15 21L9 3L6 12H2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h3>Honest Gaps</h3>
              <p>The report flags its low-confidence and unmeasured parts, so you know what to trust.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="how-it-works section">
        <div className="container">
          <div className="section-header text-center">
            <span className="section-tag">How It Works</span>
            <h2>Get started in <span className="text-gradient">3 steps</span></h2>
          </div>
          <div className="steps-minimal">
            <div className="step-minimal">
              <div className="step-number">1</div>
              <h3>Upload video</h3>
              <p>Record your practice or match and upload it to AceSense.</p>
            </div>
            <div className="step-connector"></div>
            <div className="step-minimal">
              <div className="step-number">2</div>
              <h3>Post-match analysis</h3>
              <p>Our pipeline processes the uploaded video for shots, movement, heatmaps, and confidence.</p>
            </div>
            <div className="step-connector"></div>
            <div className="step-minimal">
              <div className="step-number">3</div>
              <h3>Read the report</h3>
              <p>Review what changed, what to train next, and which sections had low confidence.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section — shared with the /pricing route (single source of
          truth for the tier numbers). See src/components/PricingTiers.tsx. */}
      <PricingTiers />

      {/* CTA Section */}
      <section className="cta section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready for your first post-match report?</h2>
            <p>Create a free account, then download AceSense on iOS or Android.</p>
            <div className="cta-buttons">
              <div className="hero-cta-primary-wrap">
                <a
                  href={SIGN_UP_URL}
                  onClick={() => trackWebAppClick('cta-create-account')}
                  className="btn btn-primary btn-large hero-cta-primary"
                  aria-label="Create a free AceSense account"
                >
                  Analyze my first match
                </a>
                <p className="hero-cta-note">Free plan · no card required</p>
              </div>
              <p className="hero-cta-step-label">Then download the app</p>
              <div className="hero-cta-stores" aria-label="Download the app after signup">
                <a
                  href={DOWNLOAD_IOS_URL}
                  onClick={() => trackAppStoreClick('cta-final-ios')}
                  className="app-store-link"
                  aria-label="Download AceSense on the App Store"
                >
                  <img
                    src="/app-store-badge.svg"
                    alt="Download on the App Store"
                    className="app-store-badge"
                    width={180}
                    height={60}
                  />
                </a>
                <a
                  href={DOWNLOAD_ANDROID_URL}
                  onClick={() => trackAppStoreClick('cta-final-android')}
                  className="app-store-link"
                  aria-label="Get AceSense for Android on Google Play"
                >
                  <img
                    src="/google-play-badge.png"
                    alt="Get it on Google Play"
                    className="app-store-badge app-store-badge-play"
                    width={200}
                    height={60}
                  />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
      </main>
      <Footer />

      {/* GDPR Cookie Consent Banner */}
      <CookieConsent />
    </div>
  )
}

export default App
